/**
 * TanStack Query client — global server-state layer for WingWise.
 *
 * Defaults are conservative on purpose:
 *   - No refetch-on-focus by default (opt-in for near-realtime queries)
 *   - Retry idempotent GETs up to 2 times with jittered backoff
 *   - Never auto-retry mutations
 *   - Keep previous data during background refresh
 *
 * Per-query stale-time tiers are exposed as constants — callers should pick
 * the tier that matches the underlying data volatility. Do NOT set stale-times
 * inline; use the constants so the whole app stays consistent.
 */
import { QueryClient } from "@tanstack/react-query";

// ---- Stale-time tiers (ms) ----
export const STALE = {
  REAL_TIME: 15_000,       // quotes, open positions, stop/target proximity
  SHORT:     3 * 60_000,   // opportunities, daily brief, regime, portfolio summary
  MEDIUM:    30 * 60_000,  // technicals, correlation, ETF overlap, news, scanner profiles
  LONG:      6 * 60 * 60_000, // fundamentals, saved analyses, market memory, coaching
};

// ---- Garbage-collection tiers (ms) ----
export const GC = {
  REAL_TIME: 60_000,
  SHORT:     10 * 60_000,
  MEDIUM:    60 * 60_000,
  LONG:      24 * 60 * 60_000,
};

const isBrowser = typeof window !== "undefined";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE.SHORT,
      gcTime:    GC.SHORT,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
      retry: (failureCount, error) => {
        // Never retry auth failures
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt + Math.random() * 250, 8_000),
      // Preserve previous data during refetch to avoid flicker.
      placeholderData: (prev) => prev,
      networkMode: isBrowser ? "online" : "always",
    },
    mutations: {
      retry: false,
      networkMode: isBrowser ? "online" : "always",
    },
  },
});
